import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {EmptyState} from '../../reportElements/charts/emptyState';
import {StyledFlex} from '../../reportViewer/reportCommons';

/*
 * Styles.
 */

const ButtonsWrapper = styled.div`
  display: flex;
  margin-top: 16px;
`;

const StyledButton = styled(Button)`
  margin: 0px 6px;
`;

const StyledIcon = styled(Icon)`
  margin-left: 4px;
  margin-top: 1px;
`;

/*
 * Props.
 */

interface SetupEmptyStateProps {
  width?: number;
  height?: number;
  title: ReactNode;
  buttonText: ReactNode;
  learnMoreLink: string;
  setupLink?: string;
  interactionId?: string;
}

/*
 * Component.
 */

/**
 * Renders an empty state used in analytics that prompts the user to set-up whatever feature is missing data.
 *
 * Note that if the `setupLink` is not provided, we only render the "Learn More" button.
 */
export const SetupEmptyState: FC<SetupEmptyStateProps> = ({
  width,
  height,
  title,
  setupLink,
  buttonText,
  learnMoreLink,
  interactionId,
}) => {
  const dispatch = useAppDispatch();

  return (
    <EmptyState
      title={title}
      width={width}
      height={height}
      subtitle={
        <ButtonsWrapper>
          <StyledButton
            variant={ButtonVariantsEnum.SECONDARY}
            onClick={() => dispatch(navigateToLinkAction(learnMoreLink))}
          >
            <StyledFlex>
              <Trans id="analytics-empty-state-no-setup-button-learn-more">Learn more</Trans>
              <StyledIcon name="externalLinkSmall" />
            </StyledFlex>
          </StyledButton>
          {setupLink && (
            <StyledButton
              variant={ButtonVariantsEnum.PRIMARY}
              onClick={() => dispatch(navigateToLinkAction(setupLink))}
              interactionId={interactionId}
            >
              {buttonText}
            </StyledButton>
          )}
        </ButtonsWrapper>
      }
    />
  );
};
